import { parseResponse } from '$lib/client/responseHelpers';
import Emitter from '$lib/services/streaming/eventsDispatcher';
import Events from '$lib/services/streaming/eventsDispatcher/events';
import type { IvsRealTimePlayerConfig, PlayerConfigCommon } from '$lib/services/streaming/types';

import { ivsRealTimeGetDeliveryConfig } from '@api/streaming/streaming';
import { streamingLoggerStore } from '@components/room/streaming-debugger/stores/logger';
import { LogType } from '@shared/types';
import { PlayerStatus } from '@shared/types/room';
import { isHttpError } from '@sveltejs/kit';

import type {
    Stage,
    StageError,
} from 'amazon-ivs-web-broadcast';

//TEMP SOLUTION JUST FOR LOG THAT IVS TEAM ASKED
function parseJwt(token:string) {
    try {
        const base64Url = token.split('.')[1];
        const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
        const jsonPayload = decodeURIComponent(
            atob(base64)
                .split('')
                .map(c => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
                .join('')
        );

        return JSON.parse(jsonPayload);
    } catch (error) {
        return null;
    }
}
export class IvsRealTimePlayer {
    private stage?: Stage;

    public async startDelivery(config: IvsRealTimePlayerConfig & PlayerConfigCommon) {
        // Known issue: IVS works only with dynamic import on SSR
        const sdk = (await (import('amazon-ivs-web-broadcast')));

        const {
            StageErrorCode,
            Stage,
            StageConnectionState,
            StageParticipantSubscribeState,
            StageEvents,
            SubscribeType,
        } = sdk;

        Emitter.emit(Events.STREAMING_STATS_SET_DELIVERY_START_TIMER);

        const playerConfig = await this.getPlayerConfig(config.stageArn);
        if (!playerConfig) {
            config.onEvent &&
            config.onEvent(PlayerStatus.Error);
            return;
        }
        const playerToken = playerConfig.playerToken;
        const strategy = {
            stageStreamsToPublish() {
                return [];
            },
            shouldPublishParticipant() {
                return false;
            },
            shouldSubscribeToParticipant() {
                return SubscribeType.AUDIO_VIDEO;
            },
        };
        this.stage = new Stage(playerToken, strategy);

        this.stage.on(StageEvents.STAGE_PARTICIPANT_SUBSCRIBE_STATE_CHANGED, (_, state) => {
            if (state === StageParticipantSubscribeState.ERRORED) {
                streamingLoggerStore.log({
                    type: LogType.AppDelivery,
                    message: 'IvsRealTime: Delivery: StageParticipantSubscribeState ERRORED',
                });
                this.statsCallback('rtc_ivs_sub_error', { errorText: '' });
            }
        });

        this.stage.on(StageEvents.ERROR, (error: StageError) => {
            streamingLoggerStore.log({
                type: LogType.AppDelivery,
                message: `IvsRealTime: Delivery: ${error.code}, ${error.category}, ${error.message} `
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                + `details: ${(error as any)?.details} `
                + `arn: ${playerConfig.stageArn} `
                + `token: ${playerConfig.playerToken}, `
                + `StringError: ${error.toString()}`,
            });

            switch (error.code) {
                case StageErrorCode.TOKEN_EXPIRED:
                case StageErrorCode.TOKEN_MALFORMED:
                case StageErrorCode.TOKEN_NOT_ALLOWED: {
                    const parsedToken = parseJwt(playerToken);
                    if (parsedToken) {
                        const exp = parsedToken.exp * 1000;
                        const iat = parsedToken.iat;
                        const now = Date.now();
                        streamingLoggerStore.log({
                            type: LogType.AppDelivery,
                            message: `IvsRealTime: Delivery: ` +
                        `Token iat: ${iat} exp: ${exp} now: ${now} exp-now=${exp - now}`,
                        });
                    } else {
                        streamingLoggerStore.log({
                            type: LogType.AppDelivery,
                            message: `IvsRealTime: Delivery: ` +
                        `Token Error cannot parse token`,
                        });
                    }
                    this.statsCallback('rtc_ivs_token_error', { errorText: error.message });
                    config.onEvent &&
                    config.onEvent(PlayerStatus.Error);
                    return;
                }
                case StageErrorCode.CODEC_MISMATCH:
                    this.statsCallback('rtc_ivs_codec_error', { errorText: error.message });
                    config.onEvent &&
                    config.onEvent(PlayerStatus.BrowserNotSupported);
                    return;
                case StageErrorCode.FAILED:
                    this.statsCallback('rtc_ivs_failed', { errorText: error.message });
                    return;
                case StageErrorCode.CANCELED:
                    this.statsCallback('rtc_ivs_canceled_operation', { errorText: error.message });
                    config.onEvent &&
                    config.onEvent(PlayerStatus.Error);
                    return;
                case StageErrorCode.TIMEOUT:
                    this.statsCallback('rtc_ivs_timeout', { errorText: error.message });
                    return;
                default:
                    this.statsCallback('rtc_ivs_delivery_error_' + error.name, { errorText: error.message });
                    config.onEvent && config.onEvent(PlayerStatus.Error);
                    return;
            }

        });

        this.stage.on(StageEvents.STAGE_CONNECTION_STATE_CHANGED, (state) => {
            switch (state) {
                case StageConnectionState.DISCONNECTED:
                    streamingLoggerStore.log({
                        type: LogType.AppDelivery,
                        message: 'IvsRealTime: Delivery: DISCONNECTED',
                    });
                    this.statsCallback('rtc_ivs_abnormally_disconnected', { errorText: '' });
                    config.onEvent && config.onEvent(PlayerStatus.Error);
                    break;
                case StageConnectionState.CONNECTING:
                    streamingLoggerStore.log({
                        type: LogType.AppDelivery,
                        message: 'IvsRealTime: Delivery: CONNECTING',
                    });
                    config.onEvent && config.onEvent(PlayerStatus.SocketOpened);
                    break;
                case StageConnectionState.CONNECTED:
                    streamingLoggerStore.log({
                        type: LogType.AppDelivery,
                        message: 'IvsRealTime: Delivery: CONNECTED',
                    });
                    config.onEvent && config.onEvent(PlayerStatus.PeerConnectionEstablished);
                    break;
                case StageConnectionState.ERRORED:
                    streamingLoggerStore.log({
                        type: LogType.AppDelivery,
                        message: 'IvsRealTime: Delivery: ERRORED',
                    });
                    this.statsCallback('rtc_ivs_delivery_state_error', {});
                    break;
            }
        });

        this.stage.on(StageEvents.STAGE_PARTICIPANT_STREAMS_ADDED, (participant, streams) => {
            streamingLoggerStore.log({
                type: LogType.AppDelivery,
                message: 'IvsRealTime: Delivery: STAGE_PARTICIPANT_STREAMS_ADDED',
            });
            if (participant.isLocal) {
                streamingLoggerStore.log({
                    type: LogType.AppDelivery,
                    message: 'IvsRealTime: Delivery: Got local participant',
                });
            }
            if (!participant.isPublishing) {
                streamingLoggerStore.log({
                    type: LogType.AppDelivery,
                    message: 'IvsRealTime: Delivery: Participant is not publishing',
                });
            }
            streamingLoggerStore.log({
                type: LogType.AppDelivery,
                message: 'IvsRealTime: Delivery: Add tracks',
            });
            streamingLoggerStore.log({
                type: LogType.AppDelivery,
                message: `IvsRealTime: Delivery: videoStopped: ${participant.videoStopped} \
audioMuted: ${participant.audioMuted}`,
            });
            const videoEl = config.video;
            const mediaStream = new MediaStream();
            streams.forEach(stream => mediaStream.addTrack(stream.mediaStreamTrack));
            videoEl.srcObject = mediaStream;
            videoEl.load();
        });

        try {
            await this.stage.join();
        } catch (error) {
            streamingLoggerStore.log(
                {
                    type: LogType.AppDelivery,
                    message: `IvsRealTime: Delivery: Error to join`,
                },
                { errorText: (error as Error).message },
                { includeExtendedData: true }
            );
        }
    }

    private async getPlayerConfig(stageArn: string) {
        try {
            const resp = await ivsRealTimeGetDeliveryConfig(stageArn);
            const pullResponse = parseResponse(resp);
            return pullResponse;
        } catch (error) {
            let errorText:string;
            if (isHttpError(error)) {
                errorText = error.body.message;
            } else {
                errorText = (error as Error).message;
            }
            this.statsCallback(
                'rtc_ivs_player_config_error',
                { errorText }
            );
            console.error(error);
            return null;
        }
    }

    public stopDelivery() {
        if (!this.stage) {
            return;
        }
        this.stage.removeAllListeners();
        this.stage.leave();
    }

    private statsCallback(event: string, data: unknown) {
        const streamingType = 'delivery';
        Emitter.emit(Events.STREAMING_STATS_WEBRTC_SEND_EVENT, event, { ...data || {}, streamingType });
    }
}